<script>
const DISMISSED_STORAGE_KEY = 'talkvia:pwa-install-dismissed-at';
const DISMISS_DURATION = 1000 * 60 * 60 * 24 * 14;

const isMobileDevice = () =>
  window.matchMedia('(max-width: 768px)').matches ||
  /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);

const isIOSDevice = () =>
  /iPhone|iPad|iPod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === 'MacIntel' &&
    window.navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const wasRecentlyDismissed = () => {
  const dismissedAt = Number(localStorage.getItem(DISMISSED_STORAGE_KEY));
  return dismissedAt && Date.now() - dismissedAt < DISMISS_DURATION;
};

export default {
  name: 'PwaInstallPrompt',
  data() {
    return {
      deferredInstallPrompt: null,
      isVisible: false,
      isIOS: false,
      showIOSInstructions: false,
      appIconPath: '/android-icon-192x192.png',
      copy: {
        ariaLabel: 'Instalar Talkvia',
        title: 'Instalar Talkvia',
        description:
          'Acesse pela tela inicial e receba notificações no celular.',
        iosInstructions:
          'No iPhone, toque em Compartilhar e depois em Adicionar à Tela de Início.',
        install: 'Instalar',
        iosInstall: 'Ver como instalar',
        dismiss: 'Agora não',
      },
    };
  },
  mounted() {
    this.isIOS = isIOSDevice();

    if (!isMobileDevice() || isStandalone() || wasRecentlyDismissed()) {
      return;
    }

    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.dismiss);

    if (this.isIOS) {
      window.setTimeout(() => {
        if (!isStandalone() && !wasRecentlyDismissed()) {
          this.isVisible = true;
        }
      }, 1500);
    }
  },
  unmounted() {
    window.removeEventListener(
      'beforeinstallprompt',
      this.onBeforeInstallPrompt
    );
    window.removeEventListener('appinstalled', this.dismiss);
  },
  methods: {
    onBeforeInstallPrompt(event) {
      event.preventDefault();
      this.deferredInstallPrompt = event;
      this.isVisible = true;
    },
    async install() {
      if (this.isIOS) {
        this.showIOSInstructions = true;
        return;
      }

      if (!this.deferredInstallPrompt) {
        return;
      }

      this.deferredInstallPrompt.prompt();
      await this.deferredInstallPrompt.userChoice;
      this.deferredInstallPrompt = null;
      this.dismiss();
    },
    dismiss() {
      localStorage.setItem(DISMISSED_STORAGE_KEY, Date.now().toString());
      this.isVisible = false;
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <section
      v-if="isVisible"
      class="pwa-install-prompt fixed inset-x-3 bottom-3 z-[9999] rounded-lg border border-n-weak bg-n-solid-1 p-4 text-n-slate-12 shadow-2xl md:hidden"
      :aria-label="copy.ariaLabel"
    >
      <div class="flex items-start gap-3">
        <img
          :src="appIconPath"
          alt=""
          class="size-11 flex-shrink-0 rounded-lg"
        />
        <div class="min-w-0 flex-1">
          <h2 class="text-sm font-semibold">{{ copy.title }}</h2>
          <p class="mt-1 text-sm leading-5 text-n-slate-11">
            {{ copy.description }}
          </p>
          <p
            v-if="showIOSInstructions"
            class="mt-3 rounded-lg bg-n-alpha-2 p-3 text-sm leading-5 text-n-slate-11"
          >
            {{ copy.iosInstructions }}
          </p>
          <div class="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-95"
              @click="install"
            >
              {{ isIOS ? copy.iosInstall : copy.install }}
            </button>
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
              @click="dismiss"
            >
              {{ copy.dismiss }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </Teleport>
</template>

<style scoped>
.pwa-install-prompt {
  padding-bottom: max(1rem, env(safe-area-inset-bottom));
}
</style>
